import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const STATUS_STYLE = {
  pending_review: "bg-amber-100 text-amber-900 border-amber-300",
  auto_sent: "bg-emerald-600 text-white border-emerald-700",
  sent: "bg-emerald-100 text-emerald-900 border-emerald-300",
  dismissed: "bg-slate-200 text-slate-700 border-slate-300",
  skipped: "bg-slate-100 text-slate-500 border-slate-200",
  error: "bg-red-100 text-red-800 border-red-300",
};
export const STATUS_LABEL = { pending_review: "Needs review", auto_sent: "Auto-sent", sent: "Sent", dismissed: "Dismissed", skipped: "Skipped", error: "Error" };
const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

function OrderChips({ m, t }) {
  if (!m.order_ids?.length) return <span className="text-xs text-slate-400" data-testid={`ai-mail-noorder-${m.id}`}>{t("No order matched")}</span>;
  return m.order_ids.map((oid, i) => (
    <Link key={oid} to={`/order/${oid}`} className="font-mono text-xs px-2 py-0.5 rounded border border-slate-300 bg-white hover:bg-slate-50" data-testid={`ai-mail-order-${m.id}-${i}`}>
      {m.order_pos?.[i] || oid} <span className="text-slate-400">· {m.order_stages?.[i] || ""}</span>
    </Link>
  ));
}

export function AiEmailItem({ m }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [subject, setSubject] = useState(m.draft_subject || "");
  const [body, setBody] = useState(m.draft_body || "");
  const [showOriginal, setShowOriginal] = useState(m.status === "pending_review");
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ai-inbox"] }); qc.invalidateQueries({ queryKey: ["alerts"] }); };
  const send = useMutation({
    mutationFn: () => api.post(`/ai-email/${m.id}/send`, { subject, body }),
    onSuccess: (r) => { refresh(); toast.success(r.data.suppressed ? t("Reply recorded (preview mode — not delivered)") : t("Reply sent")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });
  const dismiss = useMutation({
    mutationFn: () => api.post(`/ai-email/${m.id}/dismiss`),
    onSuccess: () => { refresh(); toast.success(t("Dismissed")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const regen = useMutation({
    mutationFn: () => api.post(`/ai-email/${m.id}/regenerate`),
    onSuccess: (r) => { setSubject(r.data.draft_subject || subject); setBody(r.data.draft_body || body); refresh(); toast.success(t("New draft ready")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Drafting failed"),
  });
  const pending = m.status === "pending_review" || m.status === "error";
  const answered = m.status === "auto_sent" || m.status === "sent";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3" data-testid={`ai-mail-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900" data-testid={`ai-mail-from-${m.id}`}>{m.from_name || m.from_email}</span>
            <span className="text-xs text-slate-500 font-mono">{m.from_email}</span>
            <Badge className={`border text-[10px] uppercase tracking-wider ${STATUS_STYLE[m.status] || ""}`} data-testid={`ai-mail-status-${m.id}`}>{t(STATUS_LABEL[m.status] || m.status)}</Badge>
            {m.simulated && <Badge className="border border-sky-300 bg-sky-50 text-sky-800 text-[10px]">{t("simulated")}</Badge>}
            {m.intent && <Badge className="border border-slate-300 bg-slate-50 text-slate-700 text-[10px] uppercase" data-testid={`ai-mail-intent-${m.id}`}>{m.intent} · {Math.round((m.confidence || 0) * 100)}%</Badge>}
          </div>
          <div className="text-sm text-slate-800 mt-0.5" data-testid={`ai-mail-subject-${m.id}`}>{m.subject || <i className="text-slate-400">({t("no subject")})</i>}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5"><OrderChips m={m} t={t} /></div>
        </div>
        <div className="text-[11px] font-mono text-slate-500 text-right">
          <div>{t("received")} {fmt(m.received_at || m.created_at)}</div>
          {m.sent_at && <div>{t("answered")} {fmt(m.sent_at)} · {m.sent_by}</div>}
        </div>
      </div>
      {m.hold_reasons?.length > 0 && !answered && (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`ai-mail-hold-${m.id}`}>
          ⏸ {t("Held for review")}: {m.hold_reasons.join(" · ")}
        </div>
      )}
      {m.error && <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid={`ai-mail-error-${m.id}`}>⚠ {m.error}</div>}
      <div>
        <button type="button" onClick={() => setShowOriginal((v) => !v)} className="text-xs text-slate-600 underline" data-testid={`ai-mail-toggle-original-${m.id}`}>
          {showOriginal ? t("Hide customer e-mail") : t("Show customer e-mail")}
        </button>
        {showOriginal && <pre className="mt-1 whitespace-pre-wrap text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3 max-h-60 overflow-auto font-sans" data-testid={`ai-mail-body-${m.id}`}>{m.body_text}</pre>}
      </div>
      {answered ? (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-800">{t("Reply")} · {m.sent_subject}{m.sent_suppressed ? ` · ${t("preview mode, not delivered")}` : ""}</div>
          <pre className="mt-1 whitespace-pre-wrap text-sm text-slate-800 bg-emerald-50 border border-emerald-200 rounded-md p-3 font-sans" data-testid={`ai-mail-sent-${m.id}`}>{m.sent_body}</pre>
        </div>
      ) : pending || m.status === "dismissed" ? (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{t("ChatGPT draft")} · {t("edit before sending")}</div>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="font-medium" data-testid={`ai-mail-draft-subject-${m.id}`} placeholder={t("Subject")} />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="text-sm" data-testid={`ai-mail-draft-body-${m.id}`} />
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => regen.mutate()} disabled={regen.isPending} data-testid={`ai-mail-regenerate-${m.id}`}>↻ {regen.isPending ? t("Drafting…") : t("Regenerate")}</Button>
            {m.status !== "dismissed" && <Button variant="outline" size="sm" onClick={() => dismiss.mutate()} disabled={dismiss.isPending} className="text-slate-600" data-testid={`ai-mail-dismiss-${m.id}`}>✕ {t("Dismiss")}</Button>}
            <Button size="sm" onClick={() => send.mutate()} disabled={send.isPending || !body.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ai-mail-send-${m.id}`}>✉️ {send.isPending ? t("Sending…") : t("Send reply")}</Button>
          </div>
        </div>
      ) : m.skip_reason ? (
        <div className="text-xs text-slate-500">{t("Skipped")}: {m.skip_reason}</div>
      ) : null}
    </div>
  );
}
